import React, { useRef, useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import { Box, Stack, TextField } from '@mui/material';
import { StyledModal } from 'src/presentations/components/molecules/modal';
import Button from 'src/presentations/components/atom/button';
import { FormContainer } from 'src/presentations/components/organisms/forms/FormContainer';
import { BilledEntitiesMenu } from './BilledEntitiesMenu';
import { useBilledEntities } from 'src/use-cases/settings/useBilledEntities';
import { useCreditDetails } from 'src/use-cases/settings/useCreditTransactions';
import IconDollar from 'src/assets/images/icons/dollar-circle.svg';
import IconDollarYellow from 'src/assets/images/icons/dollar-circle-yellow.svg';
import IconCalendar from 'src/assets/images/icons/filter-calendar.svg';

// Validation schema
const validationSchema = Yup.object({
  billedEntityId: Yup.string().required('Credit entity is required'),
  invoiceCredits: Yup.string()
    .required('Invoice credits is required')
    .test('is-valid-number', 'Must be a valid number', (value) => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0;
    }),
  invoiceCreditExpiryDate: Yup.date().nullable().required('Invoice credit expiry date is required').typeError('Invalid date'),
  shipmentCredits: Yup.string()
    .required('Shipment credits is required')
    .test('is-valid-number', 'Must be a valid number', (value) => {
      if (!value) return false;
      const num = parseFloat(value);
      return !isNaN(num) && num >= 0;
    }),
  shipmentCreditExpiryDate: Yup.date().nullable().required('Shipment credit expiry date is required').typeError('Invalid date'),
  poNumber: Yup.string().required('PO number is required'),
  poDate: Yup.date().nullable().required('PO date is required').typeError('Invalid date'),
}).required();

type AddCreditsFormValues = Yup.InferType<typeof validationSchema>;

interface AddCreditsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AddCreditsFormValues) => void;
  isLoading?: boolean;
}

const CustomStyledModal = styled(StyledModal)<{ width?: number }>(({ width }) => {
  const modalWidth = width ?? 540;
  return {
    '& .MuiDialog-paper': {
      maxWidth: `${modalWidth}px`,
      borderRadius: '16px',
      overflowY: 'unset',
    },
    '& .MuiDialogContent-root': {
      overflowY: 'unset',
    },
  };
});

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormLabel-root': {
    fontSize: '16px',
    color: '#6B7280',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    border: '1px solid #DFDFDF',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      borderColor: '#999999',
    },
    '&.Mui-focused': {
      borderColor: '#00992B',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

// Base input styling shared across all form inputs
const baseInputStyles = {
  width: '100%',
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    border: '1px solid #DFDFDF',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      borderColor: '#DFDFDF',
    },
    '&.Mui-focused': {
      borderColor: '#DFDFDF',
    },
    '&.Mui-error': {
      borderColor: '#d32f2f',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    color: '#404040',
    padding: '8px 8px 8px 12px',
    '@media (min-width: 1536px)': {
      fontSize: '16px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
    color: '#404040',
    backgroundColor: '#FFFFFF',
    paddingLeft: '4px',
    paddingRight: '4px',
    transform: 'translate(12px, 8px) scale(1)',
    '&.MuiInputLabel-shrink': {
      backgroundColor: '#FFFFFF',
      color: '#404040',
      transform: 'translate(12px, -9px) scale(0.75)',
    },
    '&.Mui-focused': {
      color: '#404040',
    },
    '&.Mui-error': {
      color: '#d32f2f',
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '4px',
    marginTop: '4px',
    fontSize: '12px',
    '&.Mui-error': {
      color: '#d32f2f',
    },
  },
};

const StyledTextField = styled(TextField)(() => baseInputStyles);

const StyledDatePicker = styled(DatePicker)(() => ({
  ...baseInputStyles,
  '& .MuiInputAdornment-root': {
    marginRight: '8px',
  },
}));

const EntitySelectButton = styled('button')<{ hasError?: boolean }>(({ hasError }) => ({
  width: '100%',
  minHeight: '40px',
  borderRadius: '8px',
  border: hasError ? '1px solid #d32f2f' : '1px solid #DFDFDF',
  backgroundColor: '#FFFFFF',
  color: '#404040',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 400,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 8px 8px 12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#FFFFFF',
    borderColor: hasError ? '#d32f2f' : '#DFDFDF',
  },
  '&:focus': {
    outline: 'none',
    borderColor: hasError ? '#d32f2f' : '#DFDFDF',
  },
}));

const SectionTitle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
});

const IconWrapper = styled('div')<{ bgColor: string }>(({ bgColor }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: bgColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Reusable calendar icon component
const CalendarIcon = () => <img src={IconCalendar} alt='calendar' className='w-18 h-18' />;

// Reusable grid container component
const TwoColumnGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
});

// Reusable form field components
interface CreditInputProps {
  name: 'invoiceCredits' | 'shipmentCredits' | 'poNumber';
  control: any;
  label: string;
  placeholder?: string;
  type?: string;
  error?: string;
}

const CreditInput: React.FC<CreditInputProps> = ({ name, control, label, placeholder = '', type = 'text', error }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <StyledTextField {...field} label={label} placeholder={placeholder} type={type} variant='outlined' error={!!error} helperText={error} />
    )}
  />
);

interface CreditDatePickerProps {
  name: 'invoiceCreditExpiryDate' | 'shipmentCreditExpiryDate' | 'poDate';
  control: any;
  label: string;
  error?: string;
  minDate?: Date | null;
}

const CreditDatePicker: React.FC<CreditDatePickerProps> = ({ name, control, label, error, minDate }) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <StyledDatePicker
          {...field}
          label={label}
          format='dd-MMM-yyyy'
          minDate={minDate}
          slots={{
            openPickerIcon: CalendarIcon,
          }}
          slotProps={{
            textField: {
              placeholder: '10-Dec-2025',
              error: !!error,
              helperText: error,
            },
            popper: {
              disablePortal: true,
            },
          }}
        />
      )}
    />
  </LocalizationProvider>
);

interface CreditSectionProps {
  title: string;
  icon: string;
  iconBgColor: string;
  children: React.ReactNode;
}

const CreditSection: React.FC<CreditSectionProps> = ({ title, icon, iconBgColor, children }) => (
  <div>
    <SectionTitle>
      <IconWrapper bgColor={iconBgColor}>
        <img src={icon} alt={title} className='w-20 h-20' />
      </IconWrapper>
      <span className='text-14 font-medium text-black'>{title}</span>
    </SectionTitle>
    <TwoColumnGrid>{children}</TwoColumnGrid>
  </div>
);

export const AddCreditsModal: React.FC<AddCreditsModalProps> = ({ open, onClose, onSubmit, isLoading = false }) => {
  const [isBilledEntitiesMenuVisible, setIsBilledEntitiesMenuVisible] = useState(false);
  const billedEntitiesButtonRef = useRef<HTMLButtonElement>(null);

  const { data: billedEntities } = useBilledEntities();

  const formContext = useForm<AddCreditsFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      billedEntityId: '',
      invoiceCredits: '',
      invoiceCreditExpiryDate: null,
      shipmentCredits: '',
      shipmentCreditExpiryDate: null,
      poNumber: '',
      poDate: null,
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
    trigger,
  } = formContext;
  const selectedBilledEntityId = watch('billedEntityId');

  // Fetch credit details for the selected entity to get expiration dates
  const { data: creditDetails } = useCreditDetails(selectedBilledEntityId ? Number(selectedBilledEntityId) : 0, undefined, undefined);

  // Parse expiration dates from credit details
  const invoiceMinDate = creditDetails?.invoiceCreditExpirationDate ? new Date(creditDetails.invoiceCreditExpirationDate) : null;
  const shipmentMinDate = creditDetails?.shipmentCreditExpirationDate ? new Date(creditDetails.shipmentCreditExpirationDate) : null;

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      reset({
        billedEntityId: '',
        invoiceCredits: '',
        invoiceCreditExpiryDate: null,
        shipmentCredits: '',
        shipmentCreditExpiryDate: null,
        poNumber: '',
        poDate: null,
      });
    }
  }, [open, reset]);

  // Find selected entity name
  const findEntityById = (entities: any[], targetId: number): any => {
    for (const entity of entities) {
      if (entity.id === targetId) {
        return entity;
      }
      if (entity.subEntities && entity.subEntities.length > 0) {
        const found = findEntityById(entity.subEntities, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedEntity = selectedBilledEntityId && billedEntities ? findEntityById(billedEntities, Number(selectedBilledEntityId)) : null;
  const entityDisplayText = selectedEntity?.code || 'Select Credit Entity';

  const handleBilledEntitiesToggle = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setIsBilledEntitiesMenuVisible(!isBilledEntitiesMenuVisible);
  };

  const handleBilledEntitiesClose = () => {
    setIsBilledEntitiesMenuVisible(false);
  };

  const handleBilledEntitiesApply = (selectedIds: string[]) => {
    if (selectedIds.length > 0) {
      setValue('billedEntityId', selectedIds[0]);
      trigger('billedEntityId');
    }
    setIsBilledEntitiesMenuVisible(false);
  };

  const onFormSubmit = (values: AddCreditsFormValues) => {
    onSubmit(values);
  };

  return (
    <FormContainer formContext={formContext}>
      <CustomStyledModal open={open} title='Add Credits' fullWidth onClose={!isLoading && onClose} width={527}>
        <div className='relative'>
          <StyledBox>
            <Stack spacing={3} sx={{ mt: '0.5rem' }}>
              {/* Which entity are the credits for? */}
              <div>
                <label htmlFor='entity-select' className='block text-14 font-normal text-[#475B69] mb-20'>
                  Which entity are the credits for?
                </label>
                <div className='relative'>
                  <EntitySelectButton
                    id='entity-select'
                    ref={billedEntitiesButtonRef}
                    onClick={handleBilledEntitiesToggle}
                    type='button'
                    hasError={!!errors.billedEntityId}
                  >
                    <span className={!selectedBilledEntityId ? 'text-grey-10 text-14 xl:text-16' : ''} style={{ color: errors.billedEntityId && '#d32f2f' }}>
                      {entityDisplayText}
                    </span>
                    <svg
                      className='w-20 h-20'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                      style={{
                        transform: isBilledEntitiesMenuVisible ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                    >
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                    </svg>
                  </EntitySelectButton>
                  {errors.billedEntityId && (
                    <p className='text-sm mt-1' style={{ color: '#d32f2f' }}>
                      {errors.billedEntityId.message}
                    </p>
                  )}
                  <BilledEntitiesMenu
                    billedEntities={billedEntities || []}
                    isVisible={isBilledEntitiesMenuVisible}
                    onClose={handleBilledEntitiesClose}
                    onToggle={handleBilledEntitiesToggle}
                    onApply={handleBilledEntitiesApply}
                    toggleButtonRef={billedEntitiesButtonRef}
                  />
                </div>
              </div>

              {/* Invoice Credits Section */}
              <CreditSection title='Invoice Credits' icon={IconDollar} iconBgColor='#E3F5F1'>
                <CreditInput name='invoiceCredits' control={control} label='Invoice Credits' type='number' error={errors.invoiceCredits?.message} />
                <CreditDatePicker
                  name='invoiceCreditExpiryDate'
                  control={control}
                  label='Invoice Credit Expiry Date'
                  error={errors.invoiceCreditExpiryDate?.message}
                  minDate={invoiceMinDate}
                />
              </CreditSection>

              {/* Shipment Credits Section */}
              <CreditSection title='Shipment Credits' icon={IconDollarYellow} iconBgColor='#F798241F'>
                <CreditInput name='shipmentCredits' control={control} label='Shipment Credits' type='number' error={errors.shipmentCredits?.message} />
                <CreditDatePicker
                  name='shipmentCreditExpiryDate'
                  control={control}
                  label='Shipment Credit Expiry Date'
                  error={errors.shipmentCreditExpiryDate?.message}
                  minDate={shipmentMinDate}
                />
              </CreditSection>

              {/* PO Details Section */}
              <div>
                <SectionTitle>
                  <span className='text-14 font-medium text-black'>PO Details</span>
                </SectionTitle>
                <TwoColumnGrid>
                  <CreditInput name='poNumber' control={control} label='PO Number' placeholder='200' error={errors.poNumber?.message} />
                  <CreditDatePicker name='poDate' control={control} label='PO Date' error={errors.poDate?.message} />
                </TwoColumnGrid>
              </div>
            </Stack>
          </StyledBox>

          <div className='mt-48 grid gap-16 grid-cols-2 grid-rows-1'>
            <Button variant='green_outline' onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleSubmit(onFormSubmit)} isLoading={isLoading}>
              Confirm
            </Button>
          </div>
        </div>
      </CustomStyledModal>
    </FormContainer>
  );
};
